import React, { useState } from 'react';
import { Button, Typography } from '@mui/material';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';
import { isTrackable } from './DriverTrackingMap';

// Number of driver offers waiting on a booking the customer still has to decide on.
export const offersCount = (booking) =>
  booking && booking.status === 'NEW' && booking.driverOffers && typeof booking.driverOffers === 'object'
    ? Object.keys(booking.driverOffers).length
    : 0;

// Label/value rows for one card, derived from the table's column definitions so wording,
// formatting and language keys stay identical to the desktop table. Hidden columns and the
// status column (shown in the card header) are skipped.
export const cardRows = (columns, booking) =>
  (columns || [])
    .filter((c) => c && !c.hidden && c.field !== 'status' && c.field !== 'bookingDate')
    .map((c) => ({
      key: c.field,
      label: typeof c.title === 'string' ? c.title : (c.title && c.title.props && c.title.props.text) || c.field,
      value: typeof c.render === 'function' ? c.render(booking) : booking[c.field],
    }))
    .filter((r) => r.value !== undefined && r.value !== null && r.value !== '');

export const statusColor = (status) =>
  status === 'CANCELLED' ? colors.RED : status === 'COMPLETE' ? colors.GREEN : colors.YELLOW;

// Decides what the bookings page does when live data changes. Pure, so it is unit-tested.
//   data:     live booking rows; seen: { [bookingId]: offer count already shown }
//   selected: the booking currently held by the page (may be a client copy with selectedBid)
//   blocked:  true while another modal (wallet/payment/cancel) owns `selected`
// Returns { sync, open, seen, clearBidder }:
//   sync        the live row to swap in for `selected` (null = leave it alone)
//   open        the booking whose offers pop-up should open now (null = none)
//   seen        the updated seen map (only bookings actually opened are recorded)
//   clearBidder true when the selected bidder no longer exists on the live row
export const nextOffersAction = (data, seen, selected, blocked, selectedBidder) => {
  const result = { sync: null, open: null, seen: Object.assign({}, seen || {}), clearBidder: false };
  const rows = data || [];
  if (selected && !blocked) {
    const live = rows.find((b) => b && b.id === selected.id);
    if (live && live !== selected) {
      result.sync = live;
    }
    if (live && selectedBidder && !(live.driverOffers && live.driverOffers[selectedBidder])) {
      result.clearBidder = true;
    }
  }
  if (blocked) {
    return result;
  }
  for (let i = 0; i < rows.length; i++) {
    const b = rows[i];
    const n = offersCount(b);
    if (n > 0 && (result.seen[b.id] || 0) < n) {
      if (!result.open) {
        result.open = b;
        result.seen[b.id] = n;
        if (!selected || selected.id !== b.id) {
          result.clearBidder = true; // the radio must not carry a bidder from another booking
        }
      }
    }
  }
  return result;
};

export const OFFER_HIGHLIGHT = '#FFF6CC';
const PAGE = 10;

const chipStyle = (background) => ({
  display: 'inline-block',
  backgroundColor: background,
  color: colors.WHITE,
  padding: '4px 12px',
  borderRadius: 15,
  fontWeight: 'bold',
  fontFamily: FONT_FAMILY,
  fontSize: '0.8em',
});

// Banner shown above the list/table while the customer has offers to pick from.
export function OffersBanner(props) {
  const { bookings, onView, t } = props;
  const waiting = (bookings || []).filter((b) => offersCount(b) > 0);
  if (waiting.length === 0) {
    return null;
  }
  const first = waiting[0];
  const total = waiting.reduce((n, b) => n + offersCount(b), 0);
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
        margin: '0 0 12px 0',
        padding: '10px 14px',
        borderRadius: 8,
        backgroundColor: OFFER_HIGHLIGHT,
        border: `1px solid ${colors.YELLOW}`,
        fontFamily: FONT_FAMILY,
      }}
    >
      <PlaylistAddCheckIcon style={{ color: colors.Action_Button_Back }} />
      <Typography variant="body1" style={{ flex: '1 1 200px', fontFamily: FONT_FAMILY, fontWeight: 'bold' }}>
        {t('offers_waiting', { count: total })}
        <br />
        <span style={{ fontWeight: 'normal' }}>{t('offers_waiting_hint')}</span>
      </Typography>
      <Button variant="contained" color="primary" onClick={() => onView(first)} style={{ fontFamily: FONT_FAMILY }}>
        {t('view_offers')}
      </Button>
    </div>
  );
}

// Phone layout of the customer's bookings: one card per booking, newest first as given.
export default function BookingCards(props) {
  const { bookings, columns, t, isRTL, canCancel, onCancel, onSelectBid, onPay, onOpen } = props;
  const [shown, setShown] = useState(PAGE);
  const list = bookings || [];
  const dir = isRTL === 'rtl' ? 'rtl' : 'ltr';
  const dateCol = (columns || []).find((c) => c && c.field === 'bookingDate');
  const headerDate = (b) => (b.bookingDate && dateCol && typeof dateCol.render === 'function' ? dateCol.render(b) : b.bookingDate ? new Date(b.bookingDate).toLocaleString() : b.reference);

  if (list.length === 0) {
    return (
      <Typography variant="body1" style={{ fontFamily: FONT_FAMILY, textAlign: 'center', padding: 24 }}>
        {t('no_data_available')}
      </Typography>
    );
  }

  return (
    <div style={{ direction: dir, fontFamily: FONT_FAMILY }}>
      {list.slice(0, shown).map((booking) => {
        const offers = offersCount(booking);
        const rows = cardRows(columns, booking);
        // PENDING + cash is paid to the driver in person (no action); legacy PAYMENT_PENDING keeps Pay Now.
        const payable = booking.status === 'PAYMENT_PENDING' || (booking.status === 'PENDING' && booking.payment_mode !== 'cash');
        return (
          <div
            key={booking.id}
            style={{
              backgroundColor: offers > 0 ? OFFER_HIGHLIGHT : colors.WHITE,
              border: `1px solid ${offers > 0 ? colors.YELLOW : colors.TABLE_BORDER}`,
              borderRadius: 8,
              boxShadow: '0px 2px 5px rgba(0,0,0,0.12)',
              padding: 12,
              marginBottom: 12,
            }}
          >
            <div
              onClick={onOpen ? () => onOpen(booking) : undefined}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, cursor: onOpen ? 'pointer' : 'default' }}
            >
              <Typography variant="subtitle2" style={{ fontFamily: FONT_FAMILY }}>
                {headerDate(booking)}
              </Typography>
              <span style={chipStyle(statusColor(booking.status))}>{t(booking.status)}</span>
            </div>

            {offers > 0 ? (
              <div style={{ marginTop: 8 }}>
                <span style={chipStyle(colors.Action_Button_Back)}>{t('offers_waiting', { count: offers })}</span>
              </div>
            ) : null}

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td style={{ padding: '4px 6px 4px 0', color: colors.Header_Text_back, whiteSpace: 'nowrap', verticalAlign: 'top', width: '38%' }}>{r.label}</td>
                    <td style={{ padding: '4px 0', wordBreak: 'break-word' }}>{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {offers > 0 ? (
                <Button fullWidth variant="contained" color="primary" onClick={() => onSelectBid(booking)} style={{ fontFamily: FONT_FAMILY }}>
                  {t('view_offers')} ({offers})
                </Button>
              ) : null}
              {isTrackable(booking) && onOpen ? (
                <Button fullWidth variant="contained" color="primary" onClick={() => onOpen(booking)} style={{ fontFamily: FONT_FAMILY }}>
                  {t('track_driver')}
                </Button>
              ) : null}
              {payable && onPay ? (
                <Button fullWidth variant="contained" color="primary" onClick={() => onPay(booking)} style={{ fontFamily: FONT_FAMILY }}>
                  {t('paynow_button')}
                </Button>
              ) : null}
              {canCancel && canCancel(booking) ? (
                <Button fullWidth variant="outlined" color="error" onClick={() => onCancel(booking)} style={{ fontFamily: FONT_FAMILY }}>
                  {t('cancel_booking')}
                </Button>
              ) : null}
            </div>
          </div>
        );
      })}
      {list.length > shown ? (
        <Button fullWidth variant="text" onClick={() => setShown(shown + PAGE)} style={{ fontFamily: FONT_FAMILY }}>
          {t('show_more')} ({list.length - shown})
        </Button>
      ) : null}
    </div>
  );
}